import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  ColorPicker,
  Card,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import {
  CATEGORIA_SEXOS,
  categoriaLabel,
  sexoCategoriaLabel,
} from '../../utils/categoriaLabel';

const AdministracionCategorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar categorías
  const loadCategorias = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/categorias');
      if (response.ok) {
        const data = await response.json();
        setCategorias(data);
      } else {
        message.error('Error al cargar las categorías');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategorias();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nueva categoría
  const handleCreate = () => {
    setEditingCategoria(null);
    form.resetFields();
    form.setFieldsValue({ activo: true, color: '#1890ff', sexo: 'CABALLEROS' });
    setModalVisible(true);
  };

  // Abrir modal para editar categoría
  const handleEdit = (record) => {
    setEditingCategoria(record);
    form.setFieldsValue({
      descripcion: record.cat_descripcion,
      sexo: record.cat_sexo || 'CABALLEROS',
      color: record.cat_color || '#1890ff',
      activo: record.cat_activo,
    });
    setModalVisible(true);
  };

  // Guardar categoría (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingCategoria) {
        // Actualizar
        const response = await apiClient.put(`/categorias/${editingCategoria.cat_id}`, values);
        if (response.ok) {
          message.success('Categoría actualizada correctamente');
          setModalVisible(false);
          loadCategorias();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar la categoría');
        }
      } else {
        // Crear
        const response = await apiClient.post('/categorias', values);
        if (response.ok) {
          message.success('Categoría creada correctamente');
          setModalVisible(false);
          loadCategorias();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la categoría');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error('Error al guardar la categoría');
    }
  };

  // Eliminar categoría
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/categorias/${id}`);
      if (response.ok) {
        message.success('Categoría eliminada correctamente');
        loadCategorias();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la categoría');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la categoría');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Descripción',
      dataIndex: 'cat_descripcion',
      key: 'cat_descripcion',
    },
    {
      title: 'Sexo',
      dataIndex: 'cat_sexo',
      key: 'cat_sexo',
      width: 130,
      render: (sexo) => sexoCategoriaLabel(sexo) || '-',
    },
    {
      title: 'Color',
      dataIndex: 'cat_color',
      key: 'cat_color',
      width: 120,
      render: (color) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '24px',
              height: '24px',
              backgroundColor: color || '#1890ff',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
            }}
          />
          <span style={{ fontSize: '12px', color: '#b0b0b0' }}>
            {color || '#1890ff'}
          </span>
        </div>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'cat_activo',
      key: 'cat_activo',
      width: 120,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Fecha de Creación',
      dataIndex: 'cat_fecha_creacion',
      key: 'cat_fecha_creacion',
      width: 180,
      render: (fecha) => fecha ? new Date(fecha).toLocaleString('es-AR') : '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta categoría?"
            onConfirm={() => handleDelete(record.cat_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>Administración de Categorías</h1>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCategorias}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nueva Categoría
            </Button>
          </Space>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {categorias.map(categoria => (
              <Card
                key={categoria.cat_id}
                hoverable
                style={{
                  borderLeft: `4px solid ${categoria.cat_color || '#1890ff'}`,
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {categoriaLabel(categoria)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: categoria.cat_color || '#1890ff',
                            borderRadius: '4px',
                            border: '1px solid #404040',
                          }}
                        />
                        <span style={{ fontSize: '12px', color: '#b0b0b0' }}>
                          {categoria.cat_color || '#1890ff'}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Fecha: {categoria.cat_fecha_creacion ? new Date(categoria.cat_fecha_creacion).toLocaleString('es-AR') : '-'}
                      </div>
                    </div>
                    <Tag color={categoria.cat_activo ? 'green' : 'red'}>
                      {categoria.cat_activo ? 'Activo' : 'Inactivo'}
                    </Tag>
                  </div>
                  <Space wrap>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(categoria)}
                    >
                      Editar
                    </Button>
                    <Popconfirm
                      title="¿Estás seguro de eliminar esta categoría?"
                      onConfirm={() => handleDelete(categoria.cat_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        Eliminar
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={categorias}
            loading={loading}
            rowKey="cat_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} categorías`,
            }}
          />
        )}

        {/* Modal para crear/editar */}
        <Modal
          title={editingCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="descripcion"
              label="Descripción"
              rules={[
                { required: true, message: 'La descripción es requerida' },
                { max: 100, message: 'La descripción no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ej: Principiante, Intermedio, Avanzado" />
            </Form.Item>

            <Form.Item
              name="sexo"
              label="Sexo"
              rules={[{ required: true, message: 'El sexo es requerido' }]}
            >
              <Select
                placeholder="Seleccionar sexo"
                options={CATEGORIA_SEXOS}
              />
            </Form.Item>

            <Form.Item
              name="color"
              label="Color"
              rules={[
                { required: true, message: 'El color es requerido' },
              ]}
              getValueFromEvent={(color) => {
                if (typeof color === 'string') return color;
                return color.toHexString();
              }}
            >
              <ColorPicker 
                showText 
                format="hex"
                defaultFormat="hex"
                presets={[
                  {
                    label: 'Colores recomendados',
                    colors: [
                      '#1890ff', // Azul
                      '#52c41a', // Verde
                      '#faad14', // Naranja
                      '#f5222d', // Rojo
                      '#722ed1', // Púrpura
                      '#eb2f96', // Rosa
                      '#13c2c2', // Cyan
                      '#fa8c16', // Naranja oscuro
                    ],
                  },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AdministracionCategorias;
